/**
 * Server Failover Utility
 * Handles automatic failover between primary and secondary servers
 * when connection issues are detected
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Failover configuration
const FAILOVER_CONFIG = {
  connectionTimeout: 8000, // 8 seconds
  maxRetriesPerServer: 2,
  retryDelay: 2000, // 2 seconds
};

// Storage keys
const STORAGE_KEYS = {
  assignedServer: 'kahoot_assigned_server',
  connectionAttempts: 'kahoot_connection_attempts',
  lastFailure: 'kahoot_last_failure',
};

/**
 * Get list of available servers from backend
 */
export async function getAvailableServers() {
  try {
    const response = await fetch(`${BACKEND_URL}/api/failover/servers`, {
      timeout: 5000,
    });
    
    if (response.ok) {
      const data = await response.json();
      return data.servers || [];
    }
  } catch (error) {
    console.error('Error fetching available servers:', error);
  }
  
  // Fallback to default servers
  return [
    {
      name: 'primary',
      url: 'https://play.leaderdreams.com',
      websocket_url: 'wss://play.leaderdreams.com',
      priority: 1,
    },
    {
      name: 'secondary',
      url: 'https://play.novadixital.es',
      websocket_url: 'wss://play.novadixital.es',
      priority: 2,
    },
  ];
}

/**
 * Get recommended server for a student
 */
export async function getRecommendedServer(studentId = null) {
  try {
    const url = studentId
      ? `${BACKEND_URL}/api/failover/recommend?student_id=${studentId}`
      : `${BACKEND_URL}/api/failover/recommend`;
    
    const response = await fetch(url, { timeout: 5000 });
    
    if (response.ok) {
      const data = await response.json();
      return data.server;
    }
  } catch (error) {
    console.error('Error getting recommended server:', error);
  }
  
  return null;
}

/**
 * Report a connection failure to the backend
 */
export async function reportConnectionFailure(studentId, serverName, error, type = 'socketio') {
  try {
    await fetch(`${BACKEND_URL}/api/failover/report-failure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        student_id: studentId,
        server_name: serverName,
        error: error,
        type: type,
      }),
    });
  } catch (err) {
    console.error('Error reporting failure:', err);
  }
}

/**
 * Register a successful connection
 */
export async function registerConnection(studentId, serverName, type = 'socketio') {
  try {
    await fetch(`${BACKEND_URL}/api/failover/register-connection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        student_id: studentId,
        server_name: serverName,
        type: type,
      }),
    });
  } catch (err) {
    console.error('Error registering connection:', err);
  }
}

/**
 * Unregister a connection
 */
export async function unregisterConnection(studentId, serverName) {
  try {
    await fetch(`${BACKEND_URL}/api/failover/unregister-connection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        student_id: studentId,
        server_name: serverName,
      }),
    });
  } catch (err) {
    console.error('Error unregistering connection:', err);
  }
}

/**
 * Get assigned server from localStorage or determine new one
 */
export function getAssignedServer() {
  const stored = localStorage.getItem(STORAGE_KEYS.assignedServer);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Error parsing stored server:', e);
    }
  }
  return null;
}

/**
 * Save assigned server to localStorage
 */
export function saveAssignedServer(server) {
  localStorage.setItem(STORAGE_KEYS.assignedServer, JSON.stringify(server));
}

/**
 * Clear assigned server from localStorage
 */
export function clearAssignedServer() {
  localStorage.removeItem(STORAGE_KEYS.assignedServer);
}

/**
 * Get connection attempts for a server
 */
export function getConnectionAttempts(serverName) {
  const stored = localStorage.getItem(STORAGE_KEYS.connectionAttempts);
  if (stored) {
    try {
      const attempts = JSON.parse(stored);
      return attempts[serverName] || 0;
    } catch (e) {
      return 0;
    }
  }
  return 0;
}

/**
 * Increment connection attempts for a server
 */
export function incrementConnectionAttempts(serverName) {
  const stored = localStorage.getItem(STORAGE_KEYS.connectionAttempts) || '{}';
  try {
    const attempts = JSON.parse(stored);
    attempts[serverName] = (attempts[serverName] || 0) + 1;
    localStorage.setItem(STORAGE_KEYS.connectionAttempts, JSON.stringify(attempts));
    return attempts[serverName];
  } catch (e) {
    return 1;
  }
}

/**
 * Reset connection attempts
 */
export function resetConnectionAttempts() {
  localStorage.removeItem(STORAGE_KEYS.connectionAttempts);
}

/**
 * Create Socket.IO connection with failover support
 */
export async function createSocketIOWithFailover(
  io,
  studentId,
  onConnect,
  onError,
  onStatusUpdate
) {
  const servers = await getAvailableServers();
  let currentServerIndex = 0;
  let socket = null;
  let attempts = 0;
  
  // Check if we have an assigned server
  const assignedServer = getAssignedServer();
  if (assignedServer) {
    // Find the assigned server in the list
    const index = servers.findIndex(s => s.name === assignedServer.name);
    if (index >= 0) {
      currentServerIndex = index;
      onStatusUpdate?.(`Conectando a servidor asignado: ${assignedServer.name}`);
    }
  }
  
  const tryConnect = (serverIndex) => {
    return new Promise((resolve, reject) => {
      if (serverIndex >= servers.length) {
        reject(new Error('No hay más servidores disponibles'));
        return;
      }
      
      const server = servers[serverIndex];
      const serverUrl = server.url;
      const serverName = server.name;
      
      onStatusUpdate?.(`Intentando conectar a: ${serverName}...`);
      console.log(`[FAILOVER] Connecting to ${serverName} (${serverUrl})`);
      
      const connectionTimeout = setTimeout(() => {
        console.log(`[FAILOVER] Connection timeout to ${serverName}`);
        if (socket) {
          socket.close();
        }
        reportConnectionFailure(studentId, serverName, 'Connection timeout', 'socketio');
        reject(new Error('Connection timeout'));
      }, FAILOVER_CONFIG.connectionTimeout);
      
      socket = io(serverUrl, {
        transports: ['polling', 'websocket'],
        upgrade: true,
        reconnection: true,
        reconnectionAttempts: 3,
        reconnectionDelay: 1000,
        timeout: FAILOVER_CONFIG.connectionTimeout,
      });
      
      socket.on('connect', () => {
        clearTimeout(connectionTimeout);
        console.log(`[FAILOVER] Connected successfully to ${serverName}`);
        
        // Save assigned server
        saveAssignedServer(server);
        resetConnectionAttempts();
        
        // Register connection
        registerConnection(studentId, serverName, 'socketio');
        
        onStatusUpdate?.(`✅ Conectado a: ${serverName}`);
        onConnect?.(socket, server);
        resolve(socket);
      });
      
      socket.on('connect_error', (error) => {
        clearTimeout(connectionTimeout);
        console.log(`[FAILOVER] Connection error to ${serverName}:`, error);
        
        attempts = incrementConnectionAttempts(serverName);
        reportConnectionFailure(studentId, serverName, error.message, 'socketio');
        
        if (attempts >= FAILOVER_CONFIG.maxRetriesPerServer) {
          console.log(`[FAILOVER] Max retries reached for ${serverName}, trying next server`);
          resetConnectionAttempts();
          reject(new Error(`Failed to connect to ${serverName}`));
        } else {
          onStatusUpdate?.(`⚠️ Error en ${serverName}, reintentando...`);
          setTimeout(() => reject(new Error('Retry')), FAILOVER_CONFIG.retryDelay);
        }
      });
      
      socket.on('error', (error) => {
        console.error(`[FAILOVER] Socket error on ${serverName}:`, error);
        onError?.(error);
      });
    });
  };
  
  // Try to connect with failover
  while (currentServerIndex < servers.length) {
    try {
      const connectedSocket = await tryConnect(currentServerIndex);
      return connectedSocket;
    } catch (error) {
      if (error.message === 'Retry') {
        // Retry same server
        continue;
      } else {
        // Move to next server
        currentServerIndex++;
        if (currentServerIndex < servers.length) {
          onStatusUpdate?.(`Cambiando a servidor alternativo...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
  }
  
  // All servers failed
  const errorMsg = 'No se pudo conectar a ningún servidor disponible';
  onStatusUpdate?.(`❌ ${errorMsg}`);
  throw new Error(errorMsg);
}

/**
 * Create WebSocket connection with failover support
 */
export async function createWebSocketWithFailover(
  containerId,
  studentId,
  onOpen,
  onMessage,
  onError,
  onClose,
  onStatusUpdate
) {
  const servers = await getAvailableServers();
  let currentServerIndex = 0;
  let ws = null;
  let attempts = 0;
  
  // Check if we have an assigned server
  const assignedServer = getAssignedServer();
  if (assignedServer) {
    const index = servers.findIndex(s => s.name === assignedServer.name);
    if (index >= 0) {
      currentServerIndex = index;
    }
  }
  
  const tryConnect = (serverIndex) => {
    return new Promise((resolve, reject) => {
      if (serverIndex >= servers.length) {
        reject(new Error('No hay más servidores disponibles'));
        return;
      }
      
      const server = servers[serverIndex];
      const wsUrl = `${server.websocket_url}/ws/terminal/${containerId}`;
      const serverName = server.name;
      
      onStatusUpdate?.(`Conectando terminal a: ${serverName}...`);
      console.log(`[FAILOVER-WS] Connecting to ${serverName} (${wsUrl})`);
      
      const connectionTimeout = setTimeout(() => {
        console.log(`[FAILOVER-WS] Connection timeout to ${serverName}`);
        if (ws) {
          ws.close();
        }
        reportConnectionFailure(studentId, serverName, 'WebSocket timeout', 'websocket');
        reject(new Error('Connection timeout'));
      }, FAILOVER_CONFIG.connectionTimeout);
      
      ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log(`[FAILOVER-WS] Connected successfully to ${serverName}`);
        
        // Save assigned server
        saveAssignedServer(server);
        resetConnectionAttempts();
        
        // Register connection
        registerConnection(studentId, serverName, 'websocket');
        
        onStatusUpdate?.(`✅ Terminal conectado a: ${serverName}`);
        onOpen?.(ws, server);
        resolve(ws);
      };
      
      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.log(`[FAILOVER-WS] Connection error to ${serverName}:`, error);
        
        attempts = incrementConnectionAttempts(serverName);
        reportConnectionFailure(studentId, serverName, 'WebSocket error', 'websocket');
        
        if (attempts >= FAILOVER_CONFIG.maxRetriesPerServer) {
          console.log(`[FAILOVER-WS] Max retries reached for ${serverName}, trying next server`);
          resetConnectionAttempts();
          reject(new Error(`Failed to connect to ${serverName}`));
        } else {
          onStatusUpdate?.(`⚠️ Error en terminal ${serverName}, reintentando...`);
          setTimeout(() => reject(new Error('Retry')), FAILOVER_CONFIG.retryDelay);
        }
      };
      
      ws.onmessage = (event) => {
        onMessage?.(event);
      };
      
      ws.onclose = (event) => {
        console.log(`[FAILOVER-WS] WebSocket closed for ${serverName}`);
        unregisterConnection(studentId, serverName);
        onClose?.(event);
      };
    });
  };
  
  // Try to connect with failover
  while (currentServerIndex < servers.length) {
    try {
      const connectedWs = await tryConnect(currentServerIndex);
      return connectedWs;
    } catch (error) {
      if (error.message === 'Retry') {
        // Retry same server
        continue;
      } else {
        // Move to next server
        currentServerIndex++;
        if (currentServerIndex < servers.length) {
          onStatusUpdate?.(`Cambiando terminal a servidor alternativo...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
    }
  }
  
  // All servers failed
  const errorMsg = 'No se pudo conectar el terminal a ningún servidor';
  onStatusUpdate?.(`❌ ${errorMsg}`);
  onError?.(new Error(errorMsg));
  throw new Error(errorMsg);
}

export default {
  getAvailableServers,
  getRecommendedServer,
  reportConnectionFailure,
  registerConnection,
  unregisterConnection,
  createSocketIOWithFailover,
  createWebSocketWithFailover,
  FAILOVER_CONFIG,
};
